import { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label,
  FormGroup,
  Spinner,
  Badge,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { Btn } from "../../../AbstractElements";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { useAdmin } from "../../../context/AdminContext";
import { UserOrdersModal } from "../influencerBonus/UserOrdersModal";
import { formatDate } from "../../utils/formatDate";
import { toast } from "react-toastify";
import Swal from "sweetalert2";


export const LoyaltyModal = ({ isOpen, toggle, royalty }) => {
  const { getData, postData } = useDataFetch();
  const { admin } = useAdmin();
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [filters, setFilters] = useState({ code: "", country: "" });
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryingUsers, setRetryingUsers] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Polling automático cada 5 segundos usando React Query
  const { data: settlementData, refetch: refetchSettlement } = getData(
    ["settlement-detail", royalty?._id],
    `/settlements/get/${royalty?._id}`,
    {
      enabled: isOpen && !!royalty?._id,
      refetchInterval: (query) => {
        // Acceder correctamente a paid_status desde el query data
        const paidStatus = query?.state?.data?.data?.paid_status;
        // Solo hacer polling si paid_status es true (procesando)
        if (paidStatus === true) {
          return 5000;
        }
        // Detener polling si paid_status es false o no existe
        return false;
      },
    }
  );

  const {
    data: payoutDetail,
    isLoading: loadingPayoutDetail,
  } = getData(
    ["payout-detail", royalty?._id, selectedUserId],
    "withdrawal/settlement/find-by-order-and-user",
    {
      enabled: !!royalty?._id && !!selectedUserId && isOpen,
    },
    {
      settlementId: royalty?._id,
      userId: selectedUserId,
    }
  );


  const currentSettlement = settlementData?.data || royalty;

  const { mutateAsync: triggerPaymentJob } = postData(
    '/withdrawal/settlement/trigger-payment-job',
    {},
    {
      onSuccess: (response) => {
        if (response.status === 200) {
          toast.success('Job de pagos iniciado exitosamente');
        } else {
          toast.error(response.errors || 'Error al iniciar el job de pagos');
        }
        setIsProcessing(false);
      },
      onError: (error) => {
        toast.error('Error al iniciar el job de pagos');
        console.error('Error triggering payment job:', error);
        setIsProcessing(false);
      },
    }
  );

  const { mutateAsync: retryUserPayment } = postData(
    '/withdrawal/settlement/retry-user-payment',
    {},
    {
      onSuccess: (response, variables) => {
        if (response.status === 200) {
          toast.success('Reintento de pago iniciado exitosamente');
        } else {
          toast.error(response.errors || 'Error al reintentar el pago');
        }
        setRetryingUsers(prev => ({ ...prev, [variables.userId]: false }));
      },
      onError: (error, variables) => {
        toast.error('Error al reintentar el pago');
        console.error('Error retrying user payment:', error);
        setRetryingUsers(prev => ({ ...prev, [variables.userId]: false }));
      },
    }
  );

  const handleTriggerPaymentJob = async () => {
    if (!currentSettlement?._id) return;

    // Confirmar antes de iniciar el proceso de pagos
    const result = await Swal.fire({
      title: '¿Iniciar proceso de pagos?',
      html: `
        <p>Estás a punto de iniciar el proceso de pagos para este corte de liquidación.</p>
        <p class="text-warning"><i class="fa fa-exclamation-triangle"></i> Esta acción procesará los pagos usuarios pendientes.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, iniciar pagos',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    setIsProcessing(true);
    await triggerPaymentJob({ settlementId: currentSettlement._id });
    // Refetch para obtener el paid_status actualizado y activar el polling
    await refetchSettlement();
  };

  const handleRetryUserPayment = async (userId) => {
    if (!currentSettlement?._id || !userId) return;
    setRetryingUsers(prev => ({ ...prev, [userId]: true }));
    await retryUserPayment({ settlementId: currentSettlement._id, userId });
    // Refetch para obtener el estado actualizado del pago del usuario
    await refetchSettlement();
  };

  const validOrderIds = selectedOrderIds || [];

  const { data: ordersResponse, isPending: loadingOrders } = getData(
    ["orders_by_ids", validOrderIds],
    `/order/byOrders/ids?ids=${validOrderIds.join(",")}`,
    { enabled: validOrderIds.length > 0 }
  );

  const { data: countries } = getData(["countries"], "/product/countries-available/all");
  const countryOptions = useMemo(
    () =>
      countries?.data?.countries?.map((c) => ({
        value: c.country_id,
        label: c.name,
      })) || [],
    [countries]
  );

  const filteredData = useMemo(() => {
    if (!currentSettlement?.users) return [];
    return currentSettlement.users
      .filter((u) => {
        if (filters.code && !u.user_id.multyhealth_code.toLowerCase().includes(filters.code.toLowerCase())) {
          return false;
        }
        if (filters.country && u.user_id.country_id !== filters.country) {
          return false;
        }
        return true;
      })
      .map((u) => ({
        ...u,
        user_id: u.user_id._id,
        userName: u.user_id.name,
        userCode: u.user_id.multyhealth_code,
        userEmail: u.user_id.email,
      }));
  }, [currentSettlement?.users, filters]);

  const allFilteredUsersPaidSuccess =
    filteredData.length > 0 && filteredData.every((user) => user.paid_status === "SUCCESS");

  // COMPONENTE EXPANDIBLE PARA MOSTRAR CURRENCIES
  const ExpandedComponent = ({ data }) => (
    <div className="p-2">
      {Object.entries(data.currencies || {}).map(([currency, cData]) => (
        <Card key={`${data.userCode}-${currency}`} className="shadow-sm mb-2 p-2">
          <h6 className="mb-2">
            <strong>{currency}</strong>
          </h6>

          {/* 🔹 NIVELES */}
          {cData.levels.map((level) => (
            <CardBody key={`${data.userCode}-${currency}-level-${level.level}`} className="p-1">
              <Row className="g-2 align-items-center">
                {/* Nivel con badge */}
                <Col md="2">
                  <h6>
                    <span className="badge bg-info text-dark">Nivel {level.level}</span>
                  </h6>
                </Col>

                {/* Porcentaje */}
                <Col md="2">
                  {(level.percentage * 100).toFixed(2)}%
                </Col>

                {/* Puntos */}
                <Col md="2">
                  <strong>Puntos:</strong> {level.points}
                </Col>

                {/* Valor */}
                <Col md="2">
                  <strong>Valor:</strong> {level.value.toLocaleString()}
                </Col>

                {/* Pedidos */}
                <Col md="2">
                  <strong>Pedidos:</strong> {level.orders.length}
                </Col>

                {/* Botón Ver órdenes */}
                <Col md="2" className="text-center">
                  <Btn
                    attrBtn={{
                      color: "light",
                      className: "shadow-sm border rounded-circle p-2",
                      onClick: () => setSelectedOrderIds(level.orders),
                    }}
                  >
                    <i className="fa fa-list text-primary"></i>
                  </Btn>
                </Col>
              </Row>
            </CardBody>
          ))}

          <hr />

          {/* 🔹 TOTALES DE LA MONEDA */}
          <Row className="g-2">
            <Col md="3">
              <strong>Total puntos:</strong> {cData.points}
            </Col>
            <Col md="3">
              <strong>Total valor:</strong> {cData.value.toLocaleString()}
            </Col>
            <Col md="3">
              <strong>Valor por punto:</strong> {cData.point_value.toLocaleString()}
            </Col>
            <Col md="3">
              <strong>Total pedidos:</strong>{" "}
              {cData.levels.reduce((sum, l) => sum + l.orders.length, 0)}
            </Col>
          </Row>
        </Card>
      ))}
    </div>
  );

  const handleShowPayoutDetail = (userId) => {
    setSelectedUserId(userId);
  };

  const getPaymentStatusBadge = (paidStatus) => {

    let badge = null;

    if (!paidStatus) {
      badge = <Badge color="warning">⏳ Pendiente</Badge>;
    }
    else if (paidStatus === "SUCCESS") {
      badge = <Badge color="success">✅ Pagado</Badge>;
    }
    else if (paidStatus === "PENDING") {
      badge = <Badge color="warning">⏳ Pendiente</Badge>;
    }
    else if (paidStatus === "PROCESSING") {
      badge = <Badge color="info">🔄 Procesando</Badge>;
    }
    else {
      const rejectionReason =
        paidStatus === "REJECTED_BY_MONNET"
          ? "Monnet"
          : paidStatus === "REJECTED_BY_ACCOUNT"
            ? "No tener cuenta"
            : "No cumplir los valores";

      badge = (
        <Badge color="danger">
          ❌ Rechazado ({rejectionReason})
        </Badge>
      );
    }

    return (
      <div className="d-flex align-items-center gap-2">
        {badge}
      </div>
    );
  };


  const columns = [
    { name: "Código", selector: (row) => row.userCode, center: true, width: "120px" },
    { name: "Usuario", selector: (row) => row.userName, sortable: true },
    { name: "Email", selector: (row) => row.userEmail, sortable: true },
    {
      name: "Total Pedidos",
      selector: (row) => {
        // Sumar todos los pedidos de todos los niveles de todas las currencies
        return Object.values(row.currencies || {}).reduce((total, currency) => {
          return total + currency.levels.reduce((sum, level) => sum + level.orders.length, 0);
        }, 0);
      },
      center: true,
      width: "130px",
      sortable: true
    },
    {
      name: "Valor Total",
      selector: (row) => {
        // Sumar el valor total de todas las currencies
        return Object.values(row.currencies || {}).reduce((total, currency) => {
          return total + currency.value;
        }, 0);
      },
      format: (row) => {
        const total = Object.values(row.currencies || {}).reduce((total, currency) => {
          return total + currency.value;
        }, 0);
        return total.toLocaleString();
      },
      right: true,
      width: "150px",
      sortable: true
    },
    {
      name: "Estado Pago",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">

          {getPaymentStatusBadge(row.paid_status)}

          {row.paid_status === 'REJECTED_BY_MONNET' &&
          (
            <i
              className="fa fa-eye text-primary"
              style={{ cursor: "pointer" }}
              onClick={() => handleShowPayoutDetail(row.user_id)}
            />
          )}


        </div>
      ),
      center: true,
    },
    {
      name: "Acciones",
      cell: (row) => {
        const canRetry = row.paid_status !== 'SUCCESS' && row.paid_status !== 'PENDING' && row.paid_status !== 'PROCESSING';
        const isRetrying = retryingUsers[row.user_id];

        return canRetry && isAdmin ? (
          <Btn
            attrBtn={{
              color: "primary",
              size: "sm",
              onClick: () => handleRetryUserPayment(row.user_id),
              disabled: isRetrying
            }}
          >
            {isRetrying  ? (
              <>
                <Spinner size="sm" className="me-1" />
                Reintentando...
              </>
            ) : (
              <>
                <i className="fa fa-refresh me-1"></i>
                Reintentar
              </>
            )}
          </Btn>
        ) : 'N/A';
      },
      center: true,
      width: "150px",
    },
  ];

  useEffect(() => {

    if (!payoutDetail?.data) return;

    const payout = payoutDetail.data;

    const errors = payout?.notification_payload?.errors || payout?.response_payload?.errors;

    if (errors && errors.length > 0) {

      const errorMessages = errors
        .map(err => `Código: ${err.code}<br/>Mensaje: ${err.message}`)
        .join("<br/><br/>");

      Swal.fire({
        icon: "error",
        title: "Pago rechazado",
        html: errorMessages,
        confirmButtonText: "Cerrar",
        confirmButtonColor: "#d33",
      });

    }

  }, [payoutDetail]);

  const summary = currentSettlement?.users?.length ? currentSettlement : null;
  const isPaidStatusActive = currentSettlement?.paid_status === true;
  const isAdmin = admin?.role === "ADMIN";

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl">
        <ModalHeader toggle={toggle} className="bg-warning text-dark">
          <i className="fa fa-coins me-2"></i> Detalle de regalía
        </ModalHeader>
        <ModalBody>
          {summary ? (
            <>
              {/* 🔹 TOTALES ARRIBA */}
              <Row className="mb-4 g-2">
                <Col md="3" sm="6" xs="12">
                  <Card className="shadow-sm p-2 text-center">
                    <h6 className="mb-1 text-muted">Periodo</h6>
                    <p className="mb-0 fw-bold">
                      {formatDate(summary.period.start)} -{" "}
                      {formatDate(summary.period.end)}
                    </p>
                  </Card>
                </Col>
                <Col md="3" sm="6" xs="12">
                  <Card className="shadow-sm p-2 text-center">
                    <h6 className="mb-1 text-muted">Fecha Cálculo</h6>
                    <p className="mb-0 fw-bold">{formatDate(summary.calculation_date)}</p>
                  </Card>
                </Col>
                {Object.entries(summary.total_settlement_by_currency || {}).map(([currency, totals]) => (
                  <Col md="3" sm="6" xs="12" key={currency}>
                    <Card className="shadow-sm p-2 text-center">
                      <h6 className="mb-1">{currency}</h6>
                      <p className="mb-0">
                        <strong>Total:</strong> {totals.total_value.toLocaleString()}
                      </p>
                      <p className="mb-0">
                        <strong>Puntos:</strong> {totals.total_points}
                      </p>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* 🔹 FILTROS */}
              <Row className="mb-3 g-2">
                <Col md="3" sm="6" xs="12">
                  <FormGroup>
                    <Label className="form-label mb-1">Código</Label>
                    <Input
                      type="text"
                      placeholder="Filtrar por código"
                      value={filters.code}
                      onChange={(e) => setFilters((prev) => ({ ...prev, code: e.target.value }))}
                    />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6" xs="12">
                  <FormGroup>
                    <Label className="form-label mb-1">País</Label>
                    <Select
                      classNamePrefix="react-select"
                      isClearable
                      placeholder="Filtrar por país"
                      options={countryOptions}
                      value={
                        filters.country
                          ? {
                            value: filters.country,
                            label: countryOptions.find((o) => o.value === filters.country)?.label,
                          }
                          : null
                      }
                      onChange={(option) => setFilters((prev) => ({ ...prev, country: option ? option.value : "" }))}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* 🔹 TABLA */}
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                striped
                responsive
                noDataComponent="No hay información"
                expandableRows
                expandableRowsComponent={ExpandedComponent}
              />
            </>
          ) : (
            <p className="text-muted">No hay información</p>
          )}
        </ModalBody>

        <ModalFooter>
          {isAdmin && !allFilteredUsersPaidSuccess && (
            <Btn
              attrBtn={{
                color: "success",
                className: "px-4 me-2",
                onClick: handleTriggerPaymentJob,
                disabled: isPaidStatusActive || isProcessing
              }}
            >
              {isProcessing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Procesando...
                </>
              ) : isPaidStatusActive ? (
                <>
                  <i className="fa fa-clock me-2"></i> Procesando Pagos...
                </>
              ) : (
                <>
                  <i className="fa fa-play me-2"></i> Iniciar Pagos
                </>
              )}
            </Btn>
          )}
          <Btn attrBtn={{ color: "danger", className: "px-4", onClick: toggle }}>
            <i className="fa fa-times me-2"></i> Cerrar
          </Btn>
        </ModalFooter>
      </Modal>

      <UserOrdersModal
        orderIds={validOrderIds}
        toggle={() => setSelectedOrderIds([])}
        ordersResponse={ordersResponse}
        loadingOrders={loadingOrders}
        pointsField="loyalty_points"
      />
    </>
  );
};
